import { useQuery, type QueryClient } from '@tanstack/react-query';
import { getLoyaltyConfiguration } from '@/api/loyalty';
import type { LoyaltyConfiguration } from '@/api/types';

/**
 * Whether this restaurant runs a loyalty programme the guest can use.
 *
 * Asked once per session and cached forever. It cannot change while a guest is paying one check, and it is
 * asked twice — by the check screen, deciding whether Pay goes to loyalty or straight to the tip, and by the
 * loyalty screen itself.
 */
export function loyaltyConfigurationQuery(accessToken: string) {
    return {
        queryKey: ['loyaltyConfiguration'],
        queryFn: ({ signal }: { signal?: AbortSignal }) => getLoyaltyConfiguration(accessToken, signal),
        staleTime: Infinity,
        retry: 1,
    };
}

/**
 * Starts the lookup and reports where it got to.
 *
 * Called from the check screen so the answer is in hand by the time a guest taps Pay — the same reason the
 * wallet probe starts at the tip step rather than at the moment it is needed.
 */
export function useLoyaltyConfiguration(accessToken: string) {
    return useQuery(loyaltyConfigurationQuery(accessToken));
}

/**
 * The settled answer, waiting for the lookup if it is still running.
 *
 * A failure answers "no loyalty" rather than throwing. Paying always works; the worst outcome is a member who
 * did not get asked about their points, which beats a checkout that stops on a feature they did not ask for.
 */
export async function resolveLoyaltyConfiguration(
    queryClient: QueryClient,
    accessToken: string,
): Promise<LoyaltyConfiguration | null> {
    try {
        return await queryClient.fetchQuery(loyaltyConfigurationQuery(accessToken));
    } catch {
        return null;
    }
}
